/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useGlobalError } from "@context/ErrorContext";
import { LoadingPage } from "@pages/other/LoadingPage";
import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@context/authentication";
import { useNavigate } from "react-router";
import { Profile } from "@services/housien/types";
import { OpenAPIHousien, UserService } from "@services/requests";
import { resolveFullName } from "@/shared/utils/resolve-name";
import WelcomeBreathingPulse from "@/features/welcome/WelcomeBreathingPulse";
import { getRealLocalStorage } from "@/shared/utils/storage/localStorageWrapper";
import {
  MAX_CALLBACK_ATTEMPTS,
  hasOAuthCodeInUrl,
  incrementCallbackAttempt,
  clearCallbackAttempts,
} from "./callback.attempts";
import { CallbackErrorScreen } from "./callback.error";
import { resolvePostAuthDestination } from "./callback.destination";

/**
 * AuthenticationCallback component.
 *
 * @returns {JSX.Element} React component element
 */
function AuthenticationCallback() {
  const navigate = useNavigate();
  const auth = useAuth();

  const { setGlobalError } = useGlobalError((state) => state);

  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');
  const [callbackFailed, setCallbackFailed] = useState(false);
  const attemptRecordedRef = useRef(false);

  const finishCallback = useCallback(() => {
    clearCallbackAttempts();
    const realStorage = getRealLocalStorage();
    const savedReturnPath = realStorage.getItem("returnPath");
    realStorage.removeItem("returnPath");

    resolvePostAuthDestination(savedReturnPath).then((dest) => {
      navigate(dest);
    });
  }, [navigate]);

  useEffect(() => {
    if (!auth.isLoading) {
      if (auth.isAuthenticated && auth.user) {
        clearCallbackAttempts();
        OpenAPIHousien.TOKEN = auth.user?.access_token;

        const profile = auth.user?.profile as unknown as Profile;
        const full_name = profile?.full_name || "";
        const [first_name = "", ...last_name_parts] = full_name.split(" ");
        const last_name = last_name_parts.join(" ");
        const user_name = {
          first_name,
          last_name,
        };

        const displayName = resolveFullName(
          auth.user?.profile as Record<string, unknown> | undefined,
        );

        const onApiDone = () => {
          setWelcomeName(displayName || first_name || 'User');
          setShowWelcome(true);
        };

        UserService.createUser(user_name)
          .then(() => onApiDone())
          .catch((err) => {
            if (err.status === 400)
              onApiDone();
            else {
              setGlobalError(err, navigate);
            }
          });
      } else {
        if (hasOAuthCodeInUrl()) {
          if (!attemptRecordedRef.current) {
            attemptRecordedRef.current = true;
            const attempts = incrementCallbackAttempt();
            if (attempts >= MAX_CALLBACK_ATTEMPTS) {
              setCallbackFailed(true);
              return;
            }
          }
          setTimeout(() => {
            if (!auth.isAuthenticated) {
              setCallbackFailed(true);
            }
          }, 5000);
        } else {
          navigate("/");
        }
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user]);

  if (callbackFailed) {
    return <CallbackErrorScreen navigate={navigate} />;
  }

  if (showWelcome) {
    return <WelcomeBreathingPulse userName={welcomeName} onComplete={finishCallback} />;
  }

  return <LoadingPage />;
}

export default (AuthenticationCallback);
